import type { ItemFilter } from './ItemUtil';

type AdvancedSearchQueryData = Map<string, URLSearchParams>;

class AdvancedSearchQuery {
    private dataset: AdvancedSearchQueryData = new Map();

    empty() {
        let ret = true;
        this.dataset.forEach((data) => {
            data.forEach((value) => {
                value = value.trim();
                if (value.length !== 0) {
                    ret = false;
                }
            });
        });
        return ret;
    }

    set(type: string, key: string, value: string) {
        type = type.trim();
        key = key.trim();
        const data: URLSearchParams = this.dataset.has(type)
            ? (this.dataset.get(type) as URLSearchParams)
            : new URLSearchParams();
        data.set(key, value);
        this.dataset.set(type, data);
    }

    delete(type: string, key: string) {
        type = type.trim();
        key = key.trim();
        const data: URLSearchParams = this.dataset.has(type)
            ? (this.dataset.get(type) as URLSearchParams)
            : new URLSearchParams();
        data.delete(key);
        this.dataset.set(type, data);
    }

    deleteType(type: string) {
        type = type.trim();
        this.dataset.delete(type.trim());
    }

    getQueryParams() {
        const ret: URLSearchParams = new URLSearchParams();
        this.dataset.forEach((data, type) => {
            data.forEach((value, key) => {
                value = value.trim();
                if (value.length > 0) {
                    ret.set(`${type}.${key}`, value);
                }
            });
        });
        return ret;
    }

    setByQueryString(queryString: string) {
        const query = new URLSearchParams(queryString);
        query.forEach((v, k) => {
            const [type, ...key] = k.split('.');
            // Only `<item type>.<field>` keys are search fields (not `op` or `ml_lang`).
            if (key.length === 0) {
                return;
            }
            this.set(type, key.join('.'), v);
        });
    }

    getSearchFilter(): ItemFilter {
        const regex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // The date fields are searched as year/month/mday triples, collected
        // here and turned into Unix times once all three are known.
        const toTime = (dates: Map<string, number>, name: string): number => {
            const year = dates.get(`${name}_year`) ?? Number.NaN;
            const month = dates.get(`${name}_month`) ?? Number.NaN;
            const mday = dates.get(`${name}_mday`) ?? Number.NaN;
            if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(mday)) {
                return Number.NaN;
            }
            return new Date(year, month - 1, mday).getTime() / 1000;
        };
        const dateFields: Record<string, string[]> = {
            journal: ['date_from', 'date_to'],
            article: ['meeting_date_from', 'meeting_date_to', 'date_from', 'date_to'],
        };
        const filter: ItemFilter[] = [];
        this.dataset.forEach((data, type) => {
            const filter2: ItemFilter[] = [];
            const dates = new Map<string, number>();
            const typeDateFields = dateFields[type] ?? [];
            filter2.push({
                item_type_name: type,
            });
            data.forEach((value, key) => {
                const keys = key.split('.');
                if (keys[0] === 'file' && keys.length === 3) {
                    filter2.push({
                        file: {
                            $elemMatch: {
                                file_type_name: keys[1],
                                [keys[2]]: { $regex: [regex(value), 'i'] },
                            },
                        },
                    });
                } else if (
                    typeDateFields.some((name) => ['year', 'month', 'mday'].some((unit) => key === `${name}_${unit}`))
                ) {
                    dates.set(key, parseInt(value, 10));
                } else {
                    filter2.push({ [key]: { $regex: [regex(value), 'i'] } });
                }
            });
            if (type === 'article') {
                const meetingFrom = toTime(dates, 'meeting_date_from');
                if (!Number.isNaN(meetingFrom)) {
                    filter2.push({ meeting_date_from: { $eq: meetingFrom } });
                }
                const meetingTo = toTime(dates, 'meeting_date_to');
                if (!Number.isNaN(meetingTo)) {
                    filter2.push({ meeting_date_to: { $eq: meetingTo } });
                }
            }
            if (type === 'journal' || type === 'article') {
                const from = toTime(dates, 'date_from');
                const to = toTime(dates, 'date_to');
                if (!Number.isNaN(from) && !Number.isNaN(to)) {
                    filter2.push({ date: { $between: [from, to] } });
                } else if (!Number.isNaN(from)) {
                    filter2.push({ date: { $gte: from } });
                } else if (!Number.isNaN(to)) {
                    filter2.push({ date: { $lte: to } });
                }
            }
            filter.push({ $and: filter2 });
        });
        return { $or: filter };
    }
}

export default AdvancedSearchQuery;
